/**
 * Carga diferida de una página (SOL-11).
 *
 * Cada pantalla viaja en su propio chunk y se pide la primera vez que se abre.
 * Las páginas del repo exportan con nombre y `React.lazy` espera un default:
 * esto hace el puente sin tocar cada archivo.
 *
 * No hace falta un Suspense por ruta: React Router 7 envuelve la navegación en
 * una transición, así que mientras llega el chunk se sigue viendo la pantalla
 * anterior. El indicador de carga del shell solo aparece en la primera carga.
 */
import { lazy, type ComponentType } from 'react';

// `any` en las props porque es lo que pide la firma de React.lazy.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Pagina = ComponentType<any>;

export function lazyPage<M extends Record<K, Pagina>, K extends keyof M & string>(
  cargar: () => Promise<M>,
  nombre: K,
) {
  const pagina = lazy<M[K]>(async () => ({ default: (await cargar())[nombre] }));
  // Con el nombre de la página: lo leen las DevTools y el test que recorre las
  // rutas (tests/unit/pantallas-de-trabajo.test.ts).
  return Object.assign(pagina, { displayName: nombre });
}

const CLAVE_RECARGA = 'dd-recarga-por-chunk';

/**
 * Después de un deploy, los chunks de la versión anterior dejan de existir. Una
 * pestaña abierta desde antes que navega a una pantalla que todavía no cargó
 * pide un archivo que ya no está. Vite avisa con `vite:preloadError`: se
 * recarga una vez para traer la versión nueva. Si vuelve a fallar dentro de
 * 30 s, el error sigue su curso hasta el RouteErrorBoundary, así un chunk roto
 * de verdad no queda en un bucle de recargas.
 */
export function recargarSiFaltaUnChunk() {
  window.addEventListener('vite:preloadError', (evento) => {
    let ultima = 0;
    try {
      ultima = Number(sessionStorage.getItem(CLAVE_RECARGA) ?? 0);
    } catch {
      // Sin sessionStorage (ventana privada estricta): igual se recarga una vez.
    }
    if (Date.now() - ultima < 30_000) return;
    try {
      sessionStorage.setItem(CLAVE_RECARGA, String(Date.now()));
    } catch {
      // idem
    }
    evento.preventDefault();
    window.location.reload();
  });
}
